import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "../app/store.ts";
import type { ReferenceDistance, VanishingLine } from "../app/storeTypes.ts";
import {
  COLOR_AXIS_X,
  COLOR_AXIS_Y,
  COLOR_AXIS_Z,
  COLOR_WARNING,
} from "../ui/tokens.ts";

// ---------------------------------------------------------------------------
// Touch gesture helpers
// ---------------------------------------------------------------------------

function touchDistance(a: Touch, b: Touch): number {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function touchAngle(a: Touch, b: Touch): number {
  return Math.atan2(b.clientY - a.clientY, b.clientX - a.clientX);
}

function touchCenter(a: Touch, b: Touch): [number, number] {
  return [(a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2];
}

// ---------------------------------------------------------------------------
// Gizmo handle types
// ---------------------------------------------------------------------------

type HandleKind = "tl" | "tr" | "bl" | "br" | "t" | "b" | "l" | "r";
type DragAction = "move" | "scale" | "rotate"; // used by activeAction ref

/** Size of gizmo handles in pixels */
const HANDLE_SIZE = 10;
/** Extra outward distance (from corner) that triggers rotation */
const ROTATE_ZONE = 16;
/** Axis constraint line length (half, extends both directions) */
const AXIS_LINE_HALF = 4000;

// ---------------------------------------------------------------------------
// Axis color lookup
// ---------------------------------------------------------------------------

const AXIS_COLOR: Record<string, string> = {
  x: COLOR_AXIS_X,
  y: COLOR_AXIS_Y,
  z: COLOR_AXIS_Z,
};

/** Endpoint handle radius for vanishing lines */
const VP_HANDLE_R = 5;
/** Hit-test tolerance for endpoint handles */
const VP_HIT_R = 10;
/** Hit-test tolerance for line body */
const VP_LINE_HIT = 6;
/** Minimum line length (px) below which a drawn line is discarded */
const VP_MIN_LEN = 8;

function containedImageSize(
  viewportWidth: number,
  viewportHeight: number,
  naturalWidth: number,
  naturalHeight: number,
): { fitW: number; fitH: number } {
  if (
    viewportWidth <= 0 ||
    viewportHeight <= 0 ||
    naturalWidth <= 0 ||
    naturalHeight <= 0
  ) {
    return { fitW: 0, fitH: 0 };
  }

  // Match the actual <img> styling used below: max-width/max-height with no
  // explicit width/height means the browser constrains oversized images but
  // does not upscale smaller ones.
  const scale = Math.min(
    1,
    viewportWidth / naturalWidth,
    viewportHeight / naturalHeight,
  );
  return {
    fitW: naturalWidth * scale,
    fitH: naturalHeight * scale,
  };
}

// ---------------------------------------------------------------------------
// AlignCameraOverlay — vanishing-line drawing & editing sub-component
// ---------------------------------------------------------------------------

function AlignCameraOverlay({
  containerRef,
  getImageRect,
  activeLayer,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  getImageRect: () => {
    cx: number;
    cy: number;
    fitW: number;
    fitH: number;
  } | null;
  activeLayer: {
    id: string;
    offset: [number, number];
    scale: number;
    rotation: number;
    vanishingLines?: VanishingLine[];
    groundPlaneZ?: number;
    referenceDistance?: ReferenceDistance;
  };
}) {
  const pushUndo = useStore((s) => s.pushUndo);
  const alignCameraDrawAxis = useStore((s) => s.alignCameraDrawAxis);
  const selectedVanishingLineId = useStore((s) => s.selectedVanishingLineId);
  const addVanishingLine = useStore((s) => s.addVanishingLine);
  const updateVanishingLineEndpoint = useStore(
    (s) => s.updateVanishingLineEndpoint,
  );
  const deleteVanishingLine = useStore((s) => s.deleteVanishingLine);
  const setSelectedVanishingLine = useStore((s) => s.setSelectedVanishingLine);
  const setReferenceDistance = useStore((s) => s.setReferenceDistance);

  const vanishingLines = activeLayer.vanishingLines ?? [];
  const refDist = activeLayer.referenceDistance;

  // Drawing / dragging state
  const drawingLineId = useRef<string | null>(null);
  const draggingEndpoint = useRef<{
    lineId: string;
    which: "start" | "end";
  } | null>(null);
  const draggingRefHandle = useRef<"p1" | "p2" | null>(null);
  const drawingRefDist = useRef(false);
  const undoPushedForGesture = useRef(false);

  // Convert screen coordinates to normalized image coordinates [0,1]
  const screenToNorm = useCallback(
    (clientX: number, clientY: number): [number, number] | null => {
      const rect = getImageRect();
      if (!rect || !containerRef.current) return null;
      const { cx, cy, fitW, fitH } = rect;
      const containerRect = containerRef.current.getBoundingClientRect();
      const sx = clientX - containerRect.left;
      const sy = clientY - containerRect.top;
      // Account for layer transform
      const relX = sx - cx - activeLayer.offset[0];
      const relY = sy - cy - activeLayer.offset[1];
      const rad = (-activeLayer.rotation * Math.PI) / 180;
      const localX = relX * Math.cos(rad) - relY * Math.sin(rad);
      const localY = relX * Math.sin(rad) + relY * Math.cos(rad);
      const s = activeLayer.scale;
      const nx = (localX / s + fitW / 2) / fitW;
      const ny = (localY / s + fitH / 2) / fitH;
      return [nx, ny];
    },
    [
      containerRef,
      getImageRect,
      activeLayer.offset,
      activeLayer.scale,
      activeLayer.rotation,
    ],
  );

  // Convert normalized [0,1] coordinates to viewport pixel coordinates
  const normToScreen = useCallback(
    (nx: number, ny: number): [number, number] | null => {
      const rect = getImageRect();
      if (!rect) return null;
      const { cx, cy, fitW, fitH } = rect;
      const s = activeLayer.scale;
      const localX = (nx * fitW - fitW / 2) * s;
      const localY = (ny * fitH - fitH / 2) * s;
      const rad = (activeLayer.rotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const vx = cx + activeLayer.offset[0] + localX * cos - localY * sin;
      const vy = cy + activeLayer.offset[1] + localX * sin + localY * cos;
      return [vx, vy];
    },
    [getImageRect, activeLayer.offset, activeLayer.scale, activeLayer.rotation],
  );

  // Hit-test endpoints
  const hitTestEndpoint = useCallback(
    (
      clientX: number,
      clientY: number,
    ): { lineId: string; which: "start" | "end" } | null => {
      if (!containerRef.current) return null;
      const containerRect = containerRef.current.getBoundingClientRect();
      const sx = clientX - containerRect.left;
      const sy = clientY - containerRect.top;
      for (const line of vanishingLines) {
        for (const which of ["start", "end"] as const) {
          const sp = normToScreen(line[which][0], line[which][1]);
          if (!sp) continue;
          const dx = sx - sp[0];
          const dy = sy - sp[1];
          if (dx * dx + dy * dy < VP_HIT_R * VP_HIT_R) {
            return { lineId: line.id, which };
          }
        }
      }
      return null;
    },
    [containerRef, vanishingLines, normToScreen],
  );

  // Hit-test line body (point-to-segment distance)
  const hitTestLine = useCallback(
    (clientX: number, clientY: number): string | null => {
      if (!containerRef.current) return null;
      const containerRect = containerRef.current.getBoundingClientRect();
      const px = clientX - containerRect.left;
      const py = clientY - containerRect.top;
      for (const line of vanishingLines) {
        const a = normToScreen(line.start[0], line.start[1]);
        const b = normToScreen(line.end[0], line.end[1]);
        if (!a || !b) continue;
        // Point-to-segment distance
        const abx = b[0] - a[0];
        const aby = b[1] - a[1];
        const abLen2 = abx * abx + aby * aby;
        if (abLen2 < 1) continue;
        const t = Math.max(
          0,
          Math.min(1, ((px - a[0]) * abx + (py - a[1]) * aby) / abLen2),
        );
        const cx = a[0] + t * abx;
        const cy = a[1] + t * aby;
        const dist = Math.sqrt((px - cx) ** 2 + (py - cy) ** 2);
        if (dist < VP_LINE_HIT) return line.id;
      }
      return null;
    },
    [containerRef, vanishingLines, normToScreen],
  );

  // Hit-test reference distance handles
  const hitTestRefHandle = useCallback(
    (clientX: number, clientY: number): "p1" | "p2" | null => {
      if (!refDist || !containerRef.current) return null;
      const containerRect = containerRef.current.getBoundingClientRect();
      const sx = clientX - containerRect.left;
      const sy = clientY - containerRect.top;
      for (const which of ["p1", "p2"] as const) {
        const sp = normToScreen(refDist[which][0], refDist[which][1]);
        if (!sp) continue;
        const dx = sx - sp[0];
        const dy = sy - sp[1];
        if (dx * dx + dy * dy < VP_HIT_R * VP_HIT_R) return which;
      }
      return null;
    },
    [refDist, containerRef, normToScreen],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);

      // 0. Check if clicking on a reference distance handle
      const refHit = hitTestRefHandle(e.clientX, e.clientY);
      if (refHit) {
        pushUndo();
        undoPushedForGesture.current = true;
        draggingRefHandle.current = refHit;
        return;
      }

      // 1. Check if clicking on an endpoint
      const epHit = hitTestEndpoint(e.clientX, e.clientY);
      if (epHit) {
        pushUndo();
        undoPushedForGesture.current = true;
        draggingEndpoint.current = epHit;
        setSelectedVanishingLine(epHit.lineId);
        return;
      }

      // 2. Check if clicking on a line body
      const lineHit = hitTestLine(e.clientX, e.clientY);
      if (lineHit) {
        setSelectedVanishingLine(lineHit);
        return;
      }

      // 3. Draw reference distance or new vanishing line
      if (alignCameraDrawAxis === "ref") {
        const norm = screenToNorm(e.clientX, e.clientY);
        if (!norm) return;
        pushUndo();
        undoPushedForGesture.current = true;
        setReferenceDistance(activeLayer.id, {
          p1: norm,
          p2: [...norm] as [number, number],
          length: refDist?.length ?? 1,
        });
        drawingRefDist.current = true;
        return;
      }

      if (alignCameraDrawAxis) {
        const norm = screenToNorm(e.clientX, e.clientY);
        if (!norm) return;
        pushUndo();
        undoPushedForGesture.current = true;
        const id = `vl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
        const newLine: VanishingLine = {
          id,
          axis: alignCameraDrawAxis as "x" | "y",
          start: norm,
          end: [...norm] as [number, number],
        };
        addVanishingLine(activeLayer.id, newLine);
        drawingLineId.current = id;
        setSelectedVanishingLine(id);
        return;
      }

      // 4. Click on empty space — deselect
      setSelectedVanishingLine(null);
    },
    [
      hitTestEndpoint,
      hitTestLine,
      hitTestRefHandle,
      alignCameraDrawAxis,
      screenToNorm,
      pushUndo,
      addVanishingLine,
      setSelectedVanishingLine,
      setReferenceDistance,
      activeLayer.id,
      refDist,
    ],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;

      // Drawing reference distance
      if (drawingRefDist.current) {
        const norm = screenToNorm(e.clientX, e.clientY);
        if (norm && refDist) {
          setReferenceDistance(activeLayer.id, { ...refDist, p2: norm });
        }
        return;
      }

      // Dragging a reference distance handle
      if (draggingRefHandle.current) {
        const norm = screenToNorm(e.clientX, e.clientY);
        if (norm && refDist) {
          setReferenceDistance(activeLayer.id, {
            ...refDist,
            [draggingRefHandle.current]: norm,
          });
        }
        return;
      }

      // Drawing a new line
      if (drawingLineId.current) {
        const norm = screenToNorm(e.clientX, e.clientY);
        if (norm) {
          updateVanishingLineEndpoint(
            activeLayer.id,
            drawingLineId.current,
            "end",
            norm,
          );
        }
        return;
      }

      // Dragging an endpoint
      if (draggingEndpoint.current) {
        const norm = screenToNorm(e.clientX, e.clientY);
        if (norm) {
          updateVanishingLineEndpoint(
            activeLayer.id,
            draggingEndpoint.current.lineId,
            draggingEndpoint.current.which,
            norm,
          );
        }
        return;
      }

      // Hover cursor
      if (!containerRef.current) return;
      const refHit = hitTestRefHandle(e.clientX, e.clientY);
      if (refHit) {
        containerRef.current.style.cursor = "pointer";
        return;
      }
      const epHit = hitTestEndpoint(e.clientX, e.clientY);
      if (epHit) {
        containerRef.current.style.cursor = "pointer";
        return;
      }
      const lineHit = hitTestLine(e.clientX, e.clientY);
      if (lineHit) {
        containerRef.current.style.cursor = "pointer";
        return;
      }
      containerRef.current.style.cursor = alignCameraDrawAxis
        ? "crosshair"
        : "default";
    },
    [
      screenToNorm,
      updateVanishingLineEndpoint,
      setReferenceDistance,
      activeLayer.id,
      refDist,
      containerRef,
      hitTestEndpoint,
      hitTestLine,
      hitTestRefHandle,
      alignCameraDrawAxis,
    ],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;

      // If we just finished drawing a reference distance, check if it's too short
      if (drawingRefDist.current) {
        drawingRefDist.current = false;
        const store = useStore.getState();
        const layer = store.referenceLayers.find(
          (l) => l.id === activeLayer.id,
        );
        const rd = layer?.referenceDistance;
        if (rd) {
          const a = normToScreen(rd.p1[0], rd.p1[1]);
          const b = normToScreen(rd.p2[0], rd.p2[1]);
          if (a && b) {
            const dist = Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);
            if (dist < VP_MIN_LEN) {
              setReferenceDistance(activeLayer.id, undefined);
            }
          }
        }
      }

      // If we just finished drawing a line, check if it's too short
      if (drawingLineId.current) {
        const lineId = drawingLineId.current;
        drawingLineId.current = null;
        const store = useStore.getState();
        const layer = store.referenceLayers.find(
          (l) => l.id === activeLayer.id,
        );
        const line = layer?.vanishingLines?.find((vl) => vl.id === lineId);
        if (line) {
          const a = normToScreen(line.start[0], line.start[1]);
          const b = normToScreen(line.end[0], line.end[1]);
          if (a && b) {
            const dist = Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);
            if (dist < VP_MIN_LEN) {
              deleteVanishingLine(activeLayer.id, lineId);
              setSelectedVanishingLine(null);
            }
          }
        }
      }

      draggingEndpoint.current = null;
      draggingRefHandle.current = null;
      undoPushedForGesture.current = false;
    },
    [
      activeLayer.id,
      normToScreen,
      deleteVanishingLine,
      setSelectedVanishingLine,
      setReferenceDistance,
    ],
  );

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const store = useStore.getState();
      if (store.transformMode !== "alignCamera") return;

      if (e.key === "Delete" || e.key === "Backspace") {
        if (store.selectedVanishingLineId && store.activeReferenceLayerId) {
          e.preventDefault();
          store.pushUndo();
          store.deleteVanishingLine(
            store.activeReferenceLayerId,
            store.selectedVanishingLineId,
          );
        }
        return;
      }

      const axisKey = e.key.toLowerCase();
      if (axisKey === "x" || axisKey === "y") {
        e.preventDefault();
        store.setAlignCameraDrawAxis(axisKey);
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        store.setTransformMode("refImage");
        return;
      }

      // Space — temporarily switch to navigate for camera orbit
      if (e.code === "Space" && !e.repeat) {
        e.preventDefault();
        store.setMode("navigate");
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        const store = useStore.getState();
        if (store.mode === "navigate") {
          store.setTransformMode("alignCamera");
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // Convert all lines to screen coords for SVG rendering
  const screenLines = vanishingLines
    .map((line) => {
      const a = normToScreen(line.start[0], line.start[1]);
      const b = normToScreen(line.end[0], line.end[1]);
      return { ...line, screenStart: a, screenEnd: b };
    })
    .filter((l) => l.screenStart && l.screenEnd);

  // Reference distance screen coords
  const refScreen = refDist
    ? {
        a: normToScreen(refDist.p1[0], refDist.p1[1]),
        b: normToScreen(refDist.p2[0], refDist.p2[1]),
      }
    : null;

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        pointerEvents: "auto",
        cursor: alignCameraDrawAxis ? "crosshair" : "default",
        zIndex: 10,
        touchAction: "none",
      }}
    >
      <svg
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          overflow: "visible",
        }}
      >
        {/* Vanishing lines */}
        {screenLines.map((line) => {
          const color = AXIS_COLOR[line.axis] ?? "#fff";
          const isSelected = line.id === selectedVanishingLineId;
          const a = line.screenStart!;
          const b = line.screenEnd!;
          return (
            <g key={line.id}>
              <line
                x1={a[0]}
                y1={a[1]}
                x2={b[0]}
                y2={b[1]}
                stroke={color}
                strokeWidth={isSelected ? 2.5 : 1.5}
                opacity={isSelected ? 1 : 0.7}
              />
              <line
                x1={a[0] - (b[0] - a[0]) * 3}
                y1={a[1] - (b[1] - a[1]) * 3}
                x2={b[0] + (b[0] - a[0]) * 3}
                y2={b[1] + (b[1] - a[1]) * 3}
                stroke={color}
                strokeWidth={0.5}
                strokeDasharray="4 4"
                opacity={isSelected ? 0.4 : 0.15}
              />
              <circle
                cx={a[0]}
                cy={a[1]}
                r={VP_HANDLE_R}
                fill={isSelected ? color : "rgba(0,0,0,0.5)"}
                stroke={color}
                strokeWidth={1.5}
              />
              <circle
                cx={b[0]}
                cy={b[1]}
                r={VP_HANDLE_R}
                fill={isSelected ? color : "rgba(0,0,0,0.5)"}
                stroke={color}
                strokeWidth={1.5}
              />
            </g>
          );
        })}

        {/* Reference distance */}
        {refScreen?.a && refScreen?.b && (
          <g>
            <line
              x1={refScreen.a[0]}
              y1={refScreen.a[1]}
              x2={refScreen.b[0]}
              y2={refScreen.b[1]}
              stroke={COLOR_WARNING}
              strokeWidth={2}
              strokeDasharray="6 3"
              opacity={0.9}
            />
            <circle
              cx={refScreen.a[0]}
              cy={refScreen.a[1]}
              r={VP_HANDLE_R + 1}
              fill="rgba(0,0,0,0.5)"
              stroke={COLOR_WARNING}
              strokeWidth={2}
            />
            <circle
              cx={refScreen.b[0]}
              cy={refScreen.b[1]}
              r={VP_HANDLE_R + 1}
              fill="rgba(0,0,0,0.5)"
              stroke={COLOR_WARNING}
              strokeWidth={2}
            />
          </g>
        )}
      </svg>

      {/* Hint banner */}
      <div
        style={{
          position: "absolute",
          bottom: 70,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 11,
          color: "rgba(255,255,255,0.5)",
          background: "rgba(0,0,0,0.4)",
          padding: "4px 12px",
          borderRadius: 8,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        Click+drag to draw lines &middot; Click to select &middot; Del to remove
        &middot; X/Y/Z to change axis &middot; Hold Space for camera
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * HTML overlay for reference image layers.
 *
 * Renders one <img> per visible layer. In refImage transform mode an
 * interaction layer is placed above the canvas with gizmo handles for the
 * active layer only.
 */
export function ReferenceImageOverlay() {
  const referenceLayers = useStore((s) => s.referenceLayers);
  const activeReferenceLayerId = useStore((s) => s.activeReferenceLayerId);
  const mode = useStore((s) => s.mode);
  const transformMode = useStore((s) => s.transformMode);
  const pendingAxis = useStore((s) => s.pendingAxis);
  const pushUndo = useStore((s) => s.pushUndo);

  const activeLayer =
    referenceLayers.find((l) => l.id === activeReferenceLayerId) ?? null;

  const [isDragging, setIsDragging] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isScaling, setIsScaling] = useState(false);
  const [, setIsPinching] = useState(false);
  const dragStart = useRef<[number, number]>([0, 0]);
  const offsetStart = useRef<[number, number]>([0, 0]);
  const rotationStart = useRef(0);
  const scaleStart = useRef(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgNaturals = useRef<Map<string, [number, number]>>(new Map());
  const [_imgNaturalVersion, setImgNaturalVersion] = useState(0);

  // Pinch gesture state
  const pinchStartDist = useRef(0);
  const pinchStartAngle = useRef(0);
  const pinchStartScale = useRef(1);
  const pinchStartRotation = useRef(0);
  const pinchStartCenter = useRef<[number, number]>([0, 0]);
  const pinchStartOffset = useRef<[number, number]>([0, 0]);
  const undoPushedRef = useRef(false);

  const isRefMode = mode === "transform" && transformMode === "refImage";
  const isAlignMode = mode === "transform" && transformMode === "alignCamera";

  // Track active axis constraint for ref image mode (X = horizontal, Y = vertical)
  const axisConstraint =
    pendingAxis === "x" || pendingAxis === "y" ? pendingAxis : null;

  // Get natural dimensions for the active layer
  const activeNatural = activeLayer
    ? (imgNaturals.current.get(activeLayer.id) ?? null)
    : null;

  // Clean up stale entries from imgNaturals when layers are removed
  useEffect(() => {
    const layerIds = new Set(referenceLayers.map((l) => l.id));
    for (const id of imgNaturals.current.keys()) {
      if (!layerIds.has(id)) imgNaturals.current.delete(id);
    }
  }, [referenceLayers]);

  // Re-resolve local blob URLs on tab refocus (handles migration of legacy cloud keys)
  useEffect(() => {
    if (referenceLayers.length === 0) return;
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Only refresh if any layer has an empty URL (missing/not-yet-resolved)
        const needsRefresh = useStore
          .getState()
          .referenceLayers.some((l) => l.imageKey && !l.imageUrl);
        if (needsRefresh) useStore.getState().refreshReferenceImageUrls();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [referenceLayers.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Space key — while held in refImage mode, temporarily switch to navigate
  const spaceWasRef = useRef(false);

  useEffect(() => {
    const isSpace = (e: KeyboardEvent) =>
      e.code === "Space" || e.key === " " || e.keyCode === 32;

    const onKeyDown = (e: KeyboardEvent) => {
      if (isSpace(e) && !e.repeat) {
        const store = useStore.getState();
        if (store.mode === "transform" && store.transformMode === "refImage") {
          e.preventDefault();
          spaceWasRef.current = true;
          store.setMode("navigate");
        }
      }
      if (e.key === "Backspace" && !e.repeat) {
        const store = useStore.getState();
        if (
          store.mode === "transform" &&
          store.transformMode === "refImage" &&
          store.activeReferenceLayerId
        ) {
          e.preventDefault();
          store.deleteReferenceLayer(store.activeReferenceLayerId);
        }
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (isSpace(e) && spaceWasRef.current) {
        spaceWasRef.current = false;
        useStore.getState().setTransformMode("refImage");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // ---- Compute image bounding box in viewport coordinates ----

  const getImageRect = useCallback(() => {
    if (!containerRef.current || !activeNatural) return null;
    const vw = containerRef.current.clientWidth;
    const vh = containerRef.current.clientHeight;
    const [nw, nh] = activeNatural;
    const { fitW, fitH } = containedImageSize(vw, vh, nw, nh);
    // Center of viewport
    const cx = vw / 2;
    const cy = vh / 2;
    return { cx, cy, fitW, fitH };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNatural]);

  // ---- Hit-test: find topmost visible layer at a given viewport coordinate ----

  const hitTestLayers = useCallback(
    (clientX: number, clientY: number): string | null => {
      if (!containerRef.current) return null;
      const containerRect = containerRef.current.getBoundingClientRect();
      const vw = containerRef.current.clientWidth;
      const vh = containerRef.current.clientHeight;
      const cx = vw / 2;
      const cy = vh / 2;
      const mx = clientX - containerRect.left - cx;
      const my = clientY - containerRect.top - cy;

      const layers = useStore.getState().referenceLayers;
      // Iterate in reverse (topmost layer first)
      for (let i = layers.length - 1; i >= 0; i--) {
        const layer = layers[i];
        if (!layer.visible) continue;
        const natural = imgNaturals.current.get(layer.id);
        if (!natural) continue;

        const [nw, nh] = natural;
        const { fitW, fitH } = containedImageSize(vw, vh, nw, nh);

        const s = layer.scale;
        const hw = (fitW * s) / 2;
        const hh = (fitH * s) / 2;
        const rad = (-layer.rotation * Math.PI) / 180;

        // Transform mouse into layer-local space
        const relX = mx - layer.offset[0];
        const relY = my - layer.offset[1];
        const localX = relX * Math.cos(rad) - relY * Math.sin(rad);
        const localY = relX * Math.sin(rad) + relY * Math.cos(rad);

        if (Math.abs(localX) <= hw && Math.abs(localY) <= hh) {
          return layer.id;
        }
      }
      return null;
    },
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Axis-constrained delta helper ----
  const constrainDelta = useCallback(
    (dx: number, dy: number): [number, number] => {
      if (axisConstraint === "x") return [dx, 0];
      if (axisConstraint === "y") return [0, dy];
      return [dx, dy];
    },
    [axisConstraint],
  );

  // ---- Imperative cursor (bypasses React render cycle for responsiveness) ----

  const setCursor = useCallback((cursor: string) => {
    if (containerRef.current) containerRef.current.style.cursor = cursor;
  }, []);

  // ---- Pointer events (single-finger / mouse) ----

  const activeAction = useRef<DragAction | null>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isRefMode) return;
      if (e.pointerType === "touch") return;

      // Click-to-select: if user clicks on a different layer, switch to it
      const hitLayerId = hitTestLayers(e.clientX, e.clientY);
      const store = useStore.getState();
      if (hitLayerId && hitLayerId !== store.activeReferenceLayerId) {
        store.setActiveReferenceLayer(hitLayerId);
      }

      dragStart.current = [e.clientX, e.clientY];
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      e.preventDefault();

      pushUndo();

      // Re-read after potential active layer change
      const currentStore = useStore.getState();
      const layer = currentStore.referenceLayers.find(
        (l) => l.id === currentStore.activeReferenceLayerId,
      );
      if (!layer) return;

      const action = activeAction.current ?? "move";
      if (action === "rotate") {
        setIsRotating(true);
        rotationStart.current = layer.rotation;
        setCursor("crosshair");
      } else if (action === "scale") {
        setIsScaling(true);
        scaleStart.current = layer.scale;
        setCursor("nwse-resize");
      } else {
        setIsDragging(true);
        offsetStart.current = [...layer.offset];
        setCursor("grabbing");
      }
    },
    [isRefMode, pushUndo, setCursor, hitTestLayers],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      const store = useStore.getState();
      const layerId = store.activeReferenceLayerId;
      if (!layerId) return;

      if (isRotating && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const startAngle = Math.atan2(
          dragStart.current[1] - cy,
          dragStart.current[0] - cx,
        );
        const currentAngle = Math.atan2(e.clientY - cy, e.clientX - cx);
        const delta = ((currentAngle - startAngle) * 180) / Math.PI;
        store.setRefLayerRotation(layerId, rotationStart.current + delta);
      } else if (isScaling) {
        // Scale based on drag distance from start
        const dy = dragStart.current[1] - e.clientY; // up = bigger
        const factor = 1 + dy / 200;
        store.setRefLayerScale(
          layerId,
          Math.max(0.05, scaleStart.current * factor),
        );
      } else if (isDragging) {
        const rawDx = e.clientX - dragStart.current[0];
        const rawDy = e.clientY - dragStart.current[1];
        const [dx, dy] = constrainDelta(rawDx, rawDy);
        store.setRefLayerOffset(layerId, [
          offsetStart.current[0] + dx,
          offsetStart.current[1] + dy,
        ]);
      }
    },
    [isDragging, isRotating, isScaling, constrainDelta],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      setIsDragging(false);
      setIsRotating(false);
      setIsScaling(false);
      setCursor("grab");
    },
    [setCursor],
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!isRefMode) return;
      e.stopPropagation();
      const store = useStore.getState();
      const layerId = store.activeReferenceLayerId;
      const layer = store.referenceLayers.find((l) => l.id === layerId);
      if (!layer || !layerId) return;
      const factor = e.deltaY > 0 ? 0.95 : 1.05;
      store.setRefLayerScale(layerId, layer.scale * factor);
    },
    [isRefMode],
  );

  // ---- Touch events (pinch-to-zoom/rotate + two-finger drag) ----

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e: TouchEvent) => {
      if (!isRefMode) return;
      const store = useStore.getState();
      const layer = store.referenceLayers.find(
        (l) => l.id === store.activeReferenceLayerId,
      );
      if (!layer) return;

      if (e.touches.length === 1) {
        e.preventDefault();
        if (!undoPushedRef.current) {
          pushUndo();
          undoPushedRef.current = true;
        }
        const t = e.touches[0];
        dragStart.current = [t.clientX, t.clientY];
        offsetStart.current = [...layer.offset];
        setIsDragging(true);
        setIsPinching(false);
      } else if (e.touches.length === 2) {
        e.preventDefault();
        if (!undoPushedRef.current) {
          pushUndo();
          undoPushedRef.current = true;
        }
        setIsDragging(false);
        setIsPinching(true);
        const a = e.touches[0],
          b = e.touches[1];
        pinchStartDist.current = touchDistance(a, b);
        pinchStartAngle.current = touchAngle(a, b);
        pinchStartScale.current = layer.scale;
        pinchStartRotation.current = layer.rotation;
        pinchStartCenter.current = touchCenter(a, b);
        pinchStartOffset.current = [...layer.offset];
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const store = useStore.getState();
      const layerId = store.activeReferenceLayerId;
      if (!layerId) return;

      if (e.touches.length === 2) {
        e.preventDefault();
        const a = e.touches[0],
          b = e.touches[1];
        const dist = touchDistance(a, b);
        const angle = touchAngle(a, b);
        const center = touchCenter(a, b);

        const scaleRatio = dist / pinchStartDist.current;
        store.setRefLayerScale(layerId, pinchStartScale.current * scaleRatio);

        const angleDelta = ((angle - pinchStartAngle.current) * 180) / Math.PI;
        store.setRefLayerRotation(
          layerId,
          pinchStartRotation.current + angleDelta,
        );

        const dx = center[0] - pinchStartCenter.current[0];
        const dy = center[1] - pinchStartCenter.current[1];
        store.setRefLayerOffset(layerId, [
          pinchStartOffset.current[0] + dx,
          pinchStartOffset.current[1] + dy,
        ]);
      } else if (e.touches.length === 1 && isDragging) {
        e.preventDefault();
        const t = e.touches[0];
        const rawDx = t.clientX - dragStart.current[0];
        const rawDy = t.clientY - dragStart.current[1];
        const [dx, dy] =
          axisConstraint === "x"
            ? [rawDx, 0]
            : axisConstraint === "y"
              ? [0, rawDy]
              : [rawDx, rawDy];
        store.setRefLayerOffset(layerId, [
          offsetStart.current[0] + dx,
          offsetStart.current[1] + dy,
        ]);
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        setIsDragging(false);
        setIsPinching(false);
        undoPushedRef.current = false;
      } else if (e.touches.length === 1) {
        setIsPinching(false);
        setIsDragging(true);
        const t = e.touches[0];
        dragStart.current = [t.clientX, t.clientY];
        const store = useStore.getState();
        const layer = store.referenceLayers.find(
          (l) => l.id === store.activeReferenceLayerId,
        );
        if (layer) offsetStart.current = [...layer.offset];
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [isRefMode, isDragging, axisConstraint, pushUndo]);

  // ---- Hover detection for gizmo handles (imperative cursor) ----

  const handleInteractionMouseMove = useCallback(
    (e: React.PointerEvent) => {
      // Don't update hover while actively dragging
      if (isDragging || isRotating || isScaling) return;
      if (e.pointerType === "touch") return;

      const rect = getImageRect();
      if (!rect) {
        setCursor("grab");
        return;
      }

      const { cx, cy, fitW, fitH } = rect;
      const containerRect = containerRef.current!.getBoundingClientRect();
      // Mouse position relative to viewport center
      const mx = e.clientX - containerRect.left - cx;
      const my = e.clientY - containerRect.top - cy;

      // Account for image transform (offset + rotation)
      const state = useStore.getState();
      const layer = state.referenceLayers.find(
        (l) => l.id === state.activeReferenceLayerId,
      );
      if (!layer) {
        setCursor("grab");
        return;
      }

      const ox = layer.offset[0];
      const oy = layer.offset[1];
      const s = layer.scale;
      const rad = (-layer.rotation * Math.PI) / 180;
      // Transform mouse into image-local space
      const relX = mx - ox;
      const relY = my - oy;
      const localX = relX * Math.cos(rad) - relY * Math.sin(rad);
      const localY = relX * Math.sin(rad) + relY * Math.cos(rad);

      const hw = (fitW * s) / 2;
      const hh = (fitH * s) / 2;

      // Check if near a corner (within HANDLE_SIZE + ROTATE_ZONE)
      const corners = [
        [-hw, -hh],
        [hw, -hh],
        [-hw, hh],
        [hw, hh],
      ];
      for (const [cxp, cyp] of corners) {
        const dist = Math.sqrt((localX - cxp) ** 2 + (localY - cyp) ** 2);
        if (dist < HANDLE_SIZE / 2 + 4) {
          activeAction.current = "scale";
          setCursor("nwse-resize");
          return;
        }
        if (dist < HANDLE_SIZE / 2 + ROTATE_ZONE) {
          // Only if outside the image bounds
          if (Math.abs(localX) > hw - 2 || Math.abs(localY) > hh - 2) {
            activeAction.current = "rotate";
            setCursor("crosshair");
            return;
          }
        }
      }

      // Check edge midpoints
      const edges: [number, number][] = [
        [0, -hh],
        [0, hh],
        [-hw, 0],
        [hw, 0],
      ];
      for (const [ex, ey] of edges) {
        const dist = Math.sqrt((localX - ex) ** 2 + (localY - ey) ** 2);
        if (dist < HANDLE_SIZE / 2 + 4) {
          activeAction.current = "scale";
          setCursor("nwse-resize");
          return;
        }
      }

      // Inside image = move
      if (Math.abs(localX) <= hw && Math.abs(localY) <= hh) {
        activeAction.current = "move";
        setCursor("grab");
        return;
      }

      // Outside image but not near corners
      activeAction.current = "move";
      setCursor("grab");
    },
    [isDragging, isRotating, isScaling, getImageRect, setCursor],
  );

  if (referenceLayers.length === 0) return null;

  const visibleLayers = referenceLayers.filter((l) => l.visible);
  const belowLayers = visibleLayers.filter((l) => !l.above);
  const aboveLayers = visibleLayers.filter((l) => l.above);

  const baseStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  };

  // Compute gizmo handle positions for the active layer
  const gizmoHandles = (() => {
    if (!isRefMode || !activeLayer || !activeNatural || !containerRef.current)
      return null;
    const rect = getImageRect();
    if (!rect) return null;
    const { cx, cy, fitW, fitH } = rect;
    const s = activeLayer.scale;
    const hw = (fitW * s) / 2;
    const hh = (fitH * s) / 2;
    const rad = (activeLayer.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    const transformPoint = (lx: number, ly: number): [number, number] => [
      cx + activeLayer.offset[0] + lx * cos - ly * sin,
      cy + activeLayer.offset[1] + lx * sin + ly * cos,
    ];

    const handles: { kind: HandleKind; pos: [number, number] }[] = [
      { kind: "tl", pos: transformPoint(-hw, -hh) },
      { kind: "tr", pos: transformPoint(hw, -hh) },
      { kind: "bl", pos: transformPoint(-hw, hh) },
      { kind: "br", pos: transformPoint(hw, hh) },
      { kind: "t", pos: transformPoint(0, -hh) },
      { kind: "b", pos: transformPoint(0, hh) },
      { kind: "l", pos: transformPoint(-hw, 0) },
      { kind: "r", pos: transformPoint(hw, 0) },
    ];

    // Bounding outline (4 corners)
    const outline = [
      transformPoint(-hw, -hh),
      transformPoint(hw, -hh),
      transformPoint(hw, hh),
      transformPoint(-hw, hh),
    ];

    return { handles, outline, center: transformPoint(0, 0) };
  })();

  const renderLayer = (
    layer: (typeof referenceLayers)[number],
    zIndex: number,
  ) => {
    const isMissing = !layer.imageUrl && layer.imageKey;
    const transform = `translate(${layer.offset[0]}px, ${layer.offset[1]}px) scale(${layer.scale}) rotate(${layer.rotation}deg)`;

    return (
      <div
        key={layer.id}
        style={{
          ...baseStyle,
          pointerEvents: "none",
          zIndex,
        }}
      >
        {isMissing ? (
          <div
            style={{
              width: 200,
              height: 200,
              border: "2px dashed rgba(255,255,255,0.2)",
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: layer.opacity,
              transform,
              transformOrigin: "center center",
              pointerEvents: "none",
            }}
          >
            <span
              style={{
                color: "rgba(255,255,255,0.3)",
                fontSize: 12,
                textAlign: "center",
                padding: 8,
              }}
            >
              {layer.name}
            </span>
          </div>
        ) : (
          <img
            src={layer.imageUrl}
            alt=""
            draggable={false}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              opacity: layer.opacity,
              transform,
              transformOrigin: "center center",
              pointerEvents: "none",
              willChange: "transform",
            }}
            onLoad={(e) => {
              const img = e.currentTarget;
              imgNaturals.current.set(layer.id, [
                img.naturalWidth,
                img.naturalHeight,
              ]);
              setImgNaturalVersion((v) => v + 1);
            }}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {/* Below-scene layers (z-index 1) — DOM order = render order */}
      {belowLayers.map((l) => renderLayer(l, 1))}

      {/* Above-scene layers (z-index 5) — DOM order = render order */}
      {aboveLayers.map((l) => renderLayer(l, 5))}

      {/* Interaction layer — above the canvas, refImage mode only */}
      {isRefMode && (
        <div
          ref={containerRef}
          onPointerDown={handlePointerDown}
          onPointerMove={(e) => {
            handlePointerMove(e);
            handleInteractionMouseMove(e);
          }}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onWheel={handleWheel}
          style={{
            ...baseStyle,
            pointerEvents: "auto",
            cursor: "grab",
            zIndex: 10,
            touchAction: "none",
          }}
        >
          {/* Gizmo overlay — bounding box + handles */}
          {gizmoHandles && (
            <svg
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                overflow: "visible",
              }}
            >
              {/* Bounding outline */}
              <polygon
                points={gizmoHandles.outline
                  .map(([x, y]) => `${x},${y}`)
                  .join(" ")}
                fill="none"
                stroke="rgba(90,160,232,0.6)"
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />

              {/* Corner handles */}
              {gizmoHandles.handles.map((h) => (
                <rect
                  key={h.kind}
                  x={h.pos[0] - HANDLE_SIZE / 2}
                  y={h.pos[1] - HANDLE_SIZE / 2}
                  width={HANDLE_SIZE}
                  height={HANDLE_SIZE}
                  fill="rgba(90,160,232,0.9)"
                  stroke="white"
                  strokeWidth={1}
                  rx={2}
                />
              ))}

              {/* Axis constraint line */}
              {axisConstraint && gizmoHandles.center && (
                <line
                  x1={
                    axisConstraint === "x"
                      ? gizmoHandles.center[0] - AXIS_LINE_HALF
                      : gizmoHandles.center[0]
                  }
                  y1={
                    axisConstraint === "y"
                      ? gizmoHandles.center[1] - AXIS_LINE_HALF
                      : gizmoHandles.center[1]
                  }
                  x2={
                    axisConstraint === "x"
                      ? gizmoHandles.center[0] + AXIS_LINE_HALF
                      : gizmoHandles.center[0]
                  }
                  y2={
                    axisConstraint === "y"
                      ? gizmoHandles.center[1] + AXIS_LINE_HALF
                      : gizmoHandles.center[1]
                  }
                  stroke={axisConstraint === "x" ? COLOR_AXIS_X : COLOR_AXIS_Y}
                  strokeWidth={1}
                  opacity={0.6}
                />
              )}
            </svg>
          )}

          {/* Hint banner */}
          <div
            style={{
              position: "absolute",
              bottom: 70,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: 11,
              color: "rgba(255,255,255,0.5)",
              background: "rgba(0,0,0,0.4)",
              padding: "4px 12px",
              borderRadius: 8,
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            Drag to move &middot; Handles to scale &middot; Outside corner to
            rotate &middot; X/Y to constrain &middot; Hold Space for camera
          </div>
        </div>
      )}

      {/* Align camera interaction layer */}
      {isAlignMode && activeLayer && (
        <AlignCameraOverlay
          containerRef={containerRef}
          getImageRect={getImageRect}
          activeLayer={activeLayer}
        />
      )}
    </>
  );
}
